/**
 * Frontend plan definitions.
 *
 * When adding a new gated feature, update BOTH this file and
 * config/planLimits.ts (backend) to keep them in sync.
 */

export type PlanType = 'free' | 'pro' | 'business' | 'organization' | 'dev';

export type FeatureId =
    | 'create_item'
    | 'remove_branding'
    | 'advanced_analytics'
    | 'create_rooms'
    | 'digital_signage'
    | 'create_events'
    | 'recurring_events'
    | 'custom_currency_names'
    | 'email_notifications'
    | 'api_access';

export interface PlanLimits {
    maxTeamMembers: number;                    // -1 = unlimited
    maxCollaborativeEventsPerMonth: number;    // -1 = unlimited; solo events are always free
    /**
     * The booking meter (ADR-0017). -1 = unlimited; 0 means "cannot confirm".
     *
     * Counts only confirmations of bookings this workspace **originated**.
     * Responding is NEVER metered (accepting an Offer, answering an outstanding
     * advance item, acknowledging a Settlement) — metering the responding side of
     * a two-sided network makes the cheapest party a bottleneck on the expensive
     * party's workflow, and a respondent may have no account at all.
     */
    maxConfirmedBookingsPerMonth: number;
    maxActiveRooms: number;                    // -1 = unlimited
    maxStagePlots: number;                     // -1 = unlimited
    maxCustomEmotes: number;                   // -1 = unlimited; 0 = none
    canCreateRooms: boolean;
    canCreateEvents: boolean;
    canCreateRecurringEvents: boolean;
    canRunFestivals: boolean;  // Run a Festival (ADR-0053): create one and add stages. Paid plans only; a festival made before a downgrade can still be revealed, published and cancelled
    canUseAiIntegration: boolean;  // AI clients (MCP) may make CHANGES. True on every plan since 2026-10-15; kept as the switch if that changes (utils/aiScopes.ts)
    canUseCrossWorkspaceCalendar: boolean;  // Identity-scoped /hub calendar across all of a user's workspaces
    maxOwnedWorkspaces: number;  // -1 = unlimited. Max workspaces a user may OWN; the cap is the best owned plan's value. See docs/adr/0004.
    canReceiveTips: boolean;  // Accept real-money tips + cash out via Stripe Connect (all tiers)
    qualifiesSupporterBadge: boolean;  // Following this workspace earns the fan a global Supporter badge (paid plans only)
    features: FeatureId[];
}

export const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
    free: {
        canUseAiIntegration: true,
        canUseCrossWorkspaceCalendar: false,
        maxOwnedWorkspaces: 1,
        canReceiveTips: true,
        qualifiesSupporterBadge: false,
        maxTeamMembers: 2,
        maxCollaborativeEventsPerMonth: 2,
        maxConfirmedBookingsPerMonth: 4,  // ADR-0017: sized for the periodic booker
        maxActiveRooms: 1,
        maxStagePlots: 3,
        maxCustomEmotes: 0,
        canCreateRooms: true,
        canCreateEvents: true,
        canCreateRecurringEvents: false,
        canRunFestivals: false,
        // Free genuinely runs one live Room (PRICING_STRATEGY §free: "Active rooms: 1").
        // `canCreateRooms`/`maxActiveRooms` already granted it and the server already
        // honours both; this list was the only thing still refusing, which hid the whole
        // Live surface from the tier the fast-start flows exist for.
        // Deliberately NOT `digital_signage`: the free tier is "uploads: profile image only".
        features: ['create_rooms'],
    },
    pro: {
        canUseAiIntegration: true,
        canUseCrossWorkspaceCalendar: true,
        maxOwnedWorkspaces: 3,
        canReceiveTips: true,
        qualifiesSupporterBadge: true,
        maxTeamMembers: 10,
        maxCollaborativeEventsPerMonth: 20,
        maxConfirmedBookingsPerMonth: -1,
        maxActiveRooms: 5,
        maxStagePlots: -1,
        maxCustomEmotes: 10,
        canCreateRooms: true,
        canCreateEvents: true,
        canCreateRecurringEvents: true,
        canRunFestivals: true,
        features: ['remove_branding', 'create_rooms', 'digital_signage', 'create_events', 'recurring_events', 'email_notifications', 'custom_currency_names'],
    },
    business: {
        canUseAiIntegration: true,
        canUseCrossWorkspaceCalendar: true,
        maxOwnedWorkspaces: -1,
        canReceiveTips: true,
        qualifiesSupporterBadge: true,
        maxTeamMembers: -1,
        maxCollaborativeEventsPerMonth: -1,
        maxConfirmedBookingsPerMonth: -1,
        maxActiveRooms: -1,
        maxStagePlots: -1,
        maxCustomEmotes: 10,
        canCreateRooms: true,
        canCreateEvents: true,
        canCreateRecurringEvents: true,
        canRunFestivals: true,
        features: ['remove_branding', 'advanced_analytics', 'create_rooms', 'digital_signage', 'create_events', 'recurring_events', 'email_notifications', 'custom_currency_names', 'api_access'],
    },
    // Legacy alias — 'organization' maps to 'business' limits
    organization: {
        canUseAiIntegration: true,
        canUseCrossWorkspaceCalendar: true,
        maxOwnedWorkspaces: -1,
        canReceiveTips: true,
        qualifiesSupporterBadge: true,
        maxTeamMembers: -1,
        maxCollaborativeEventsPerMonth: -1,
        maxConfirmedBookingsPerMonth: -1,
        maxActiveRooms: -1,
        maxStagePlots: -1,
        maxCustomEmotes: 10,
        canCreateRooms: true,
        canCreateEvents: true,
        canCreateRecurringEvents: true,
        canRunFestivals: true,
        features: ['remove_branding', 'advanced_analytics', 'create_rooms', 'digital_signage', 'create_events', 'recurring_events', 'email_notifications', 'custom_currency_names', 'api_access'],
    },
    dev: {
        canUseAiIntegration: true,
        canUseCrossWorkspaceCalendar: true,
        maxOwnedWorkspaces: -1,
        canReceiveTips: true,
        qualifiesSupporterBadge: true,
        maxTeamMembers: -1,
        maxCollaborativeEventsPerMonth: -1,
        maxConfirmedBookingsPerMonth: -1,
        maxActiveRooms: -1,
        maxStagePlots: -1,
        maxCustomEmotes: -1,
        canCreateRooms: true,
        canCreateEvents: true,
        canCreateRecurringEvents: true,
        canRunFestivals: true,
        features: ['create_item', 'remove_branding', 'advanced_analytics', 'create_rooms', 'digital_signage', 'create_events', 'recurring_events', 'email_notifications', 'custom_currency_names', 'api_access'],
    },
};

/**
 * Normalize plan name: 'organization' → 'business' for backwards compat.
 */
export function normalizePlan(plan: string): PlanType {
    if (plan === 'organization') return 'business';
    return (plan as PlanType) || 'free';
}

export interface PlanResolutionEntity {
    plan?: string | null;
    complimentaryPlan?: string | null;
    complimentaryUntil?: string | Date | null;
}

/**
 * The plan a workspace should be treated as for feature gating. Mirrors
 * the backend's getEffectivePlan() — comp grants beat the stored plan
 * when active. Null `complimentaryUntil` means no expiry.
 */
export function getEffectivePlan(entity: PlanResolutionEntity | null | undefined): PlanType {
    if (!entity) return 'free';
    const stored = normalizePlan(entity.plan || 'free');
    const compPlan = entity.complimentaryPlan;
    if (!compPlan) return stored;
    const until = entity.complimentaryUntil;
    if (until !== null && until !== undefined) {
        const expiresAt = until instanceof Date ? until : new Date(until);
        if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
            return stored;
        }
    }
    return normalizePlan(compPlan);
}

// Legacy compat: PLAN_FEATURES maps plan → feature list
export const PLAN_FEATURES: Record<PlanType, FeatureId[]> = Object.fromEntries(
    Object.entries(PLAN_LIMITS).map(([plan, limits]) => [plan, limits.features])
) as Record<PlanType, FeatureId[]>;

export const canAccessFeature = (plan: string, feature: FeatureId): boolean => {
    const planType = normalizePlan(plan);
    const features = PLAN_FEATURES[planType];
    return features ? features.includes(feature) : false;
};

export const canPerformAction = (plan: string, action: keyof PlanLimits): boolean => {
    const planType = normalizePlan(plan);
    const limits = PLAN_LIMITS[planType];
    if (!limits) return false;
    const value = limits[action];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    return false;
};

export const getPlanLimit = (plan: string, limitKey: keyof PlanLimits): number => {
    const planType = normalizePlan(plan);
    const limits = PLAN_LIMITS[planType];
    if (!limits) return 0;
    const value = limits[limitKey];
    if (typeof value === 'number') return value;
    return 0;
};
